import {
  OwFFormFieldKey,
  OwFFormFieldType,
} from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/index";
import {
  getVisibleFieldKeys,
  useBuildResidentialAddressSchema,
} from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/helpers";
import {
  IOwFFormFieldSchema,
  OwFFormFieldValueType,
} from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import { useIsNMIApplication } from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useIsSoftCreditCheckApplication } from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsSoftCreditCheckApplication";
import { useValidatePartialFormData } from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useValidatePartialFormData";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import { useStrictLocalization } from "@faire/web/common/localization";

import { useIsOptimizedSCC } from "./useIsOptimizedSCC";

interface IUseBasicInfo {
  formData: IRetailerLaunchWithFaireApplication;
  setValue: (
    fieldName: OwFFormFieldKey,
    newValue: OwFFormFieldValueType
  ) => void;
  isResidentialAddressFormVisible: boolean;
  toggleResidentialAddressForm: () => void;
  isSignUp?: boolean;
}

export const useBasicInfo = ({
  formData,
  setValue,
  isResidentialAddressFormVisible,
  toggleResidentialAddressForm,
  isSignUp,
}: IUseBasicInfo) => {
  const { strictLocalize } = useStrictLocalization();
  const { buildResidentialAddressSchema } = useBuildResidentialAddressSchema();
  const { validatePartialFormData } = useValidatePartialFormData();

  const requiredBasicFieldKeys = useBasicInfoRequiredKeys({
    isSignUp,
  });

  const visibleBasicInfoKeys: OwFFormFieldKey[] = getVisibleFieldKeys(
    [
      "full_name",
      "first_name",
      "last_name",
      "date_of_birth",
      "residential_address",
    ],
    requiredBasicFieldKeys
  );

  const getBasicInfoErrors = () =>
    validatePartialFormData({
      formData,
      keysToValidate: requiredBasicFieldKeys,
      isResidentialAddressFormVisible,
    });

  const basicInfoFields: IOwFFormFieldSchema[] = visibleBasicInfoKeys.map(
    (key) => {
      switch (key) {
        case "residential_address":
          return buildResidentialAddressSchema({
            key,
            isResidentialAddressFormVisible,
            setValue,
            toggleResidentialAddressForm,
          });
        case "first_name":
          return {
            fieldType: OwFFormFieldType.INPUT,
            fieldName: "first_name",
            labelText: strictLocalize({
              id: "jyZNs7",
              defaultMessage: "Legal first name",
              description: {
                text: "Question in Open with Faire application form",
                img: "https://cdn.faire.com/fastly/e5edabd3f77a655a7a989864b63c97af4f0e79e4bf03a2b62413636fd93bd37a.png",
              },
            }),
            required: true,
            maxLength: undefined,
            isWrappedField: true,
            fullWidth: true,
            hideCharacterCount: true,
          };
        case "last_name":
          return {
            fieldType: OwFFormFieldType.INPUT,
            fieldName: "last_name",
            labelText: strictLocalize({
              id: "KCDT0N",
              defaultMessage: "Legal last name",
              description: {
                text: "Question in Open with Faire application form",
                img: "https://cdn.faire.com/fastly/e5edabd3f77a655a7a989864b63c97af4f0e79e4bf03a2b62413636fd93bd37a.png",
              },
            }),
            required: true,
            isWrappedField: true,
            fullWidth: true,
            hideCharacterCount: true,
          };
        case "date_of_birth":
          return {
            fieldType: OwFFormFieldType.US_DATE,
            fieldName: "date_of_birth",
            labelText: strictLocalize({
              id: "dudefJ",
              defaultMessage: "Date of birth",
              description: {
                text: "Question in Open with Faire application form",
                img: "https://cdn.faire.com/fastly/e5edabd3f77a655a7a989864b63c97af4f0e79e4bf03a2b62413636fd93bd37a.png",
              },
            }),
            required: true,
          };
        case "full_name":
        default:
          return {
            fieldType: OwFFormFieldType.INPUT,
            fieldName: "full_name",
            labelText: strictLocalize({
              id: "EkLtgm",
              defaultMessage: "What's your full name?",
              description: {
                text: "Question in Open with Faire application form",
                img: "https://cdn.faire.com/fastly/e5edabd3f77a655a7a989864b63c97af4f0e79e4bf03a2b62413636fd93bd37a.png",
              },
            }),
            required: true,
          };
      }
    }
  );

  return {
    visibleBasicInfoKeys,
    getBasicInfoErrors,
    basicInfoFields,
  };
};

const useBasicInfoRequiredKeys = ({
  isSignUp,
}: {
  isSignUp: boolean | undefined;
}): OwFFormFieldKey[] => {
  const { isNMIApplication, isNoHitNMI, isFrozenCreditNMI } =
    useIsNMIApplication();
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  const isOptimizedSCC = useIsOptimizedSCC();

  if (isNMIApplication) {
    if (isNoHitNMI) {
      return [
        "first_name",
        "last_name",
        "date_of_birth",
        "residential_address",
      ];
    } else if (isFrozenCreditNMI) {
      return [];
    }
  }
  if (isOptimizedSCC) {
    return ["first_name", "last_name", "date_of_birth", "residential_address"];
  }

  if (isSoftCreditCheckApplication) {
    return ["residential_address"];
  }
  if (!isSignUp) {
    return ["full_name", "residential_address"];
  }
  return [];
};
